/**
 * Display-only localisation for vehicle catalogue labels (makes, series,
 * variants and generation years) served by the vehicle checker API.
 *
 * The API only ever returns English names, and those names are also the
 * identity used for URL params, localStorage, result logging and lookups on
 * return visits. Nothing here mutates that data: callers translate at the
 * point of render only, so the tool keeps matching on the canonical value.
 *
 * With no rules configured the translator is inert and every label is
 * returned untouched.
 */
class VehicleLabelTranslator {
  constructor (rules) {
    this.cache = new Map()
    this.pairs = VehicleLabelTranslator.parseRules(rules)
    this.pattern = this.pairs.length ? VehicleLabelTranslator.buildPattern(this.pairs) : null
    this.lookup = new Map(this.pairs.map(([source, target]) => [source.toLowerCase(), target]))
  }

  get active () {
    return this.pattern !== null
  }

  static parseRules (rules) {
    if (!rules || typeof rules !== 'string') return []
    const pairs = []
    const seen = new Set()
    rules.split(/\r?\n/).forEach((line) => {
      const trimmed = line.trim()
      if (!trimmed || trimmed.startsWith('#')) return
      const separator = trimmed.indexOf('=')
      if (separator === -1) return
      const source = trimmed.slice(0, separator).trim()
      const target = trimmed.slice(separator + 1).trim()
      if (!source || !target) return
      const key = source.toLowerCase()
      if (seen.has(key)) return
      seen.add(key)
      pairs.push([source, target])
    })
    return pairs.sort((a, b) => b[0].length - a[0].length)
  }

  static escapeRegExp (value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  static buildPattern (pairs) {
    const sources = pairs.map(([source]) => VehicleLabelTranslator.escapeRegExp(source)).join('|')
    try {
      return new RegExp(`(^|[^\\p{L}\\p{N}])(${sources})(?![\\p{L}\\p{N}])`, 'giu')
    } catch (error) {
      console.error('Vehicle label translations could not be compiled:', error)
      return null
    }
  }

  static matchCase (source, target) {
    // Untranslated rules (english = english) must leave the label exactly as it came
    if (source.toLowerCase() === target.toLowerCase()) return source
    // Abbreviations stay abbreviations (LWB -> LWB), but don't shout a whole phrase
    if (source.length > 1 && source === source.toUpperCase() &&
        !/\s/.test(target) && target !== target.toUpperCase()) {
      return target.toUpperCase()
    }
    const firstSourceChar = source.charAt(0)
    const firstTargetChar = target.charAt(0)
    if (firstSourceChar !== firstSourceChar.toLowerCase() &&
        firstTargetChar === firstTargetChar.toLowerCase()) {
      return firstTargetChar.toUpperCase() + target.slice(1)
    }
    return target
  }

  display (value) {
    if (!this.pattern || typeof value !== 'string' || value === '') return value
    if (this.cache.has(value)) return this.cache.get(value)
    this.pattern.lastIndex = 0
    const translated = value.replace(this.pattern, (match, prefix, token) => {
      const target = this.lookup.get(token.toLowerCase())
      if (target === undefined) return match
      return prefix + VehicleLabelTranslator.matchCase(token, target)
    })
    this.cache.set(value, translated)
    return translated
  }
}

window.VehicleLabelTranslator = window.VehicleLabelTranslator || VehicleLabelTranslator
